<template>
  <div class="detail-field-date relative w-full">
    <form-field-date
      :label="label"
      :empty_field="false"
      :placeholder="placeholder"
      :model-value="selectedDate"
      name="arrival"
      input-type="date"
      :hide-label="true"
      :is-slim="true"
      :hidden="false"
      :monospace="false"
      :show-confirm-button="false"
      @update:model-value="dynamicSelectedDate"
      @toggle-date-picker="toggleShowSelectedDatePicker"
    ></form-field-date>
    <div
      v-show="showSelectedDatePicker"
      ref="datePickerRef"
      class="absolute left-0 top-full z-50 flex flex-col items-center rounded-lg border bg-white p-4 shadow-md"
    >
      <div class="flex flex-col gap-2">
        <date-picker
          :initial-date="initialSelectedDate"
          :field="'datepicker_identity_alert'"
          :handle-input="setSelectedArrivalDay"
          date-format="MM/DD/YYYY"
        ></date-picker>

        <div class="mx-2 my-2 flex flex-col items-center">
          <div class="flex items-center gap-3">
            <div class="flex w-6 flex-col items-center">
              <button
                type="button"
                class="rounded-full hover:bg-gray-100"
                @click="increaseHour"
              >
                <span class="sr-only">Next hour</span>
                <ChevronUpIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
              <input
                :value="formattedHour"
                class="border-gray my-1 h-12 w-8 rounded border p-0 text-center"
                maxLength="2"
                @input="handleHourInput"
              />
              <button
                type="button"
                class="rounded-full hover:bg-gray-100"
                @click="decreaseHour"
              >
                <span class="sr-only">Previous hour</span>
                <ChevronDownIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
            </div>

            <span>:</span>

            <div class="flex w-6 flex-col items-center">
              <button
                type="button"
                class="rounded-full hover:bg-gray-100"
                @click="increaseMinute"
              >
                <span class="sr-only">Next minute</span>

                <ChevronUpIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </button>

              <input
                :value="formattedMinute"
                class="border-gray my-1 h-12 w-8 rounded border p-0 text-center"
                maxLength="2"
                @input="handleMinuteInput"
              />
              <button
                type="button"
                class="rounded-full hover:bg-gray-100"
                @click="decreaseMinute"
              >
                <span class="sr-only">Previous minute</span>

                <ChevronDownIcon
                  class="h-5 w-5"
                  aria-hidden="true"
                />
              </button>
            </div>

            <button-main
              type="button"
              class="cursor-pointer"
              @click="handleAmPm"
            >
              {{ amPm }}
            </button-main>
          </div>
        </div>
      </div>
      <button-main
        type="button"
        class="cursor-pointer"
        @click="handleConfirmManualDateSelection"
      >
        Set Pickup Date
      </button-main>
    </div>
  </div>
</template>
<script>
import { ref, computed, watch } from 'vue';
// import { useStore } from 'vuex';
import dayjs from 'dayjs';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/solid';
import debounce from 'lodash.debounce';
// import { useRouter } from 'vue-router';
// import { useToast } from 'vue-toast-notification';

import FormFieldDate from '@/components/FormFieldDate.vue';
import DatePicker from '@/components/DatePicker.vue';
import ButtonMain from '@/components/Button.vue';
import { useClickAway } from '@/utils/useClickAway';

export default {
  name: 'Detail Field Date Picker',
  components: {
    FormFieldDate,
    DatePicker,
    ChevronUpIcon,
    ChevronDownIcon,
    ButtonMain,
  },
  props: {
    label: String,
    placeholder: String,
    loadId: Number,
    stop: Object,
    type: String,
  },
  emits: ['setPickupDate'],
  setup(props, { emit }) {
    // const store = useStore();
    // const router = useRouter();
    // const toast = useToast();

    const datePickerRef = ref(null);

    const stopTime = dayjs(props.stop?.stopDateRaw).tz(props.stop?.timezone);
    console.log(' stopTime', stopTime);
    const selectedTime = ref(stopTime);

    const increaseHour = () => {
      selectedTime.value = selectedTime.value.add(1, 'hour');
    };
    const decreaseHour = () => {
      selectedTime.value = selectedTime.value.subtract(1, 'hour');
    };
    const increaseMinute = () => {
      selectedTime.value = selectedTime.value.add(1, 'minute');
    };
    const decreaseMinute = () => {
      selectedTime.value = selectedTime.value.subtract(1, 'minute');
    };

    const formattedHour = computed(() => {
      return selectedTime.value.format('hh');
    });

    const formattedMinute = computed(() => {
      return selectedTime.value.format('mm');
    });

    const handleHourInput = debounce(async (number) => {
      const currentMinute = selectedTime.value.minute();

      selectedTime.value = dayjs()
        .set('hour', number?.target?.value)
        .set('minute', currentMinute);
    }, 500);

    const handleMinuteInput = debounce(async (number) => {
      const currentHour = selectedTime.value.hour();

      selectedTime.value = dayjs()
        .set('hour', currentHour)
        .set('minute', number?.target?.value);
    }, 500);

    const selectedDate = ref(selectedTime.value.format('M/D/YYYY, hh:mm A'));

    // Watch for changes in selectedTime

    const showSelectedDatePicker = ref(false);

    useClickAway(datePickerRef, () => {
      showSelectedDatePicker.value = false;
    });

    const dynamicSelectedDate = (dateSelected, timeSelected) => {
      console.log(' dynamicSelectedDate', dateSelected, timeSelected);
      emit('setPickupDate', selectedTime.value);
      if (timeSelected) {
        const dateStringNotFormatted = dayjs(
          `${dateSelected} ${timeSelected.format('hh:mm')} ${amPm.value}`,
          'YYYY-MM-DD hh:mm A'
        );
        selectedDate.value = dateStringNotFormatted.format('M/D/YYYY, hh:mm A');
        console.log(' selectedDate', selectedDate.value);
        return;
      }
      console.log(' dateSelected', dateSelected);
      selectedDate.value = dayjs(dateSelected).format('M/D/YYYY');
    };

    const toggleShowSelectedDatePicker = () => {
      showSelectedDatePicker.value = !showSelectedDatePicker.value;
    };

    const initialSelectedDate = computed(() => {
      return selectedTime.value.format('YYYY-MM-DD');
    });

    const selectedDateRef = ref(selectedTime.value.format('YYYY-MM-DD'));

    const setSelectedArrivalDay = (dateSelected) => {
      console.log(' setSelectedArrivalDay', dateSelected);
      if (dateSelected) {
        selectedDateRef.value = dateSelected;
        dynamicSelectedDate(dateSelected);
      }
    };

    const amPm = ref(selectedTime.value.format('A'));

    const handleAmPm = () => {
      if (amPm.value === 'AM') {
        amPm.value = 'PM';
      } else {
        amPm.value = 'AM';
      }
    };

    watch(selectedTime, (newVal) => {
      console.log(' selectedTime changed:', newVal);
      dynamicSelectedDate(selectedDateRef.value, newVal);
    });

    watch(amPm, (newVal) => {
      console.log('amPm changed:', newVal);
      dynamicSelectedDate(selectedDateRef.value, selectedTime.value);
    });

    // const manuallyUpdateLoadLogs = async (date) => {
    //   let updatedToast;
    //   updatedToast = toast.open({
    //     message: `Updating ${props.type === 'arrived_at_stop' ? 'arrival' : 'departed'} log...`,
    //     position: 'top-right',
    //     duration: 5000,
    //   });

    //   const res = await store.dispatch('loads/manuallyUpdateLoadLogs', {
    //     id: props.loadId,
    //     stop_id: props.stop.id,
    //     type: props.type,
    //     date,
    //   });

    //   try {
    //     if ([200, 201, 204].includes(res?.status) && res.data) {
    //       store.dispatch('loads/getLoad', router.currentRoute.value.params.id);
    //       store.dispatch('loads/getLoadLogs', {
    //         id: router.currentRoute.value.params.id,
    //       });

    //       toast.open({
    //         type: 'success',
    //         message: 'Load log updated',
    //         position: 'top-right',
    //         duration: 5000,
    //       });
    //     } else {
    //       toast.open({
    //         type: 'error',
    //         message: 'You are not authorized to update the load',
    //         position: 'top-right',
    //         duration: 5000,
    //       });
    //     }
    //   } catch {
    //     toast.open({
    //       type: 'error',
    //       message: 'Error updating Load Log',
    //       position: 'top-right',
    //       duration: 5000,
    //     });
    //   } finally {
    //     updatedToast?.dismiss();
    //   }
    // };

    const handleConfirmManualDateSelection = () => {
      // dynamicSelectedDate(
      //   selectedDate.value || selectedTime.value.format('YYYY-MM-DD'),
      //   selectedTime.value
      // );
      // const date = selectedDate.value || now.format('YYYY-MM-DD');
      // const formattedDate = dayjs(
      //   `${date} ${selectedTime.value.format('hh:mm')} ${amPm.value}`,
      //   'YYYY-MM-DD hh:mm A'
      // );

      console.log(' selectedTime', selectedTime.value);
      console.log(' formattedDate', selectedDate.value);

      console.log(
        'selectedTime with timezone',
        selectedTime.value.tz(props.stop?.timezone)
      );

      // Convert to ISO string with timezone offset
      const isoStringWithTimezone = selectedTime.value
        .tz(props.stop?.timezone)
        .format();
      console.log('ISO string with timezone:', isoStringWithTimezone);

      // Alternative: Use toISOString() but it will be in UTC
      const utcIsoString = selectedTime.value
        .tz(props.stop?.timezone)
        .toISOString();
      console.log('UTC ISO string:', utcIsoString);

      // Convert to string without timezone
      emit('setPickupDate', selectedTime.value);

      // manuallyUpdateLoadLogs(formattedDate);
      showSelectedDatePicker.value = false;
    };

    return {
      datePickerRef,
      selectedDate,
      dynamicSelectedDate,
      showSelectedDatePicker,
      toggleShowSelectedDatePicker,
      initialSelectedDate,
      setSelectedArrivalDay,
      formattedHour,
      formattedMinute,
      increaseHour,
      decreaseHour,
      increaseMinute,
      decreaseMinute,
      handleConfirmManualDateSelection,
      handleMinuteInput,
      handleHourInput,
      amPm,
      handleAmPm,
    };
  },
};
</script>

<style>
.detail-field-date input {
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
